import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

// Story 063 — gate org-admin-only pages. Layered on top of the auth
// check (must be signed in) plus an isOrgAdmin check sourced from the
// JWT's app_metadata.is_org_admin (Supabase puts app_metadata on the
// session.user object).
//
// Renders an inline "forbidden" message rather than redirecting,
// because this state usually means a director (or accidental visitor)
// followed an admin-only link — a redirect would loop them back through
// the auth flow with no progress.
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  const isOrgAdmin =
    (user.app_metadata as { is_org_admin?: boolean } | undefined)
      ?.is_org_admin === true;

  if (!isOrgAdmin) {
    return (
      <div
        data-testid="forbidden-message"
        className="flex min-h-screen items-center justify-center bg-background p-6"
      >
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-2xl font-semibold">{t('route_guards.forbidden_title')}</h1>
          <p className="text-muted-foreground">
            {t('route_guards.forbidden_body')}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
